import { createContext, useContext, ReactNode } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { usePublicConfig } from '@/hooks/usePublicConfig';

const libraries: ("places" | "geometry" | "visualization")[] = ['places', 'geometry', 'visualization'];

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
  apiKey: string;
  isConfigLoading: boolean;
  configError: Error | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | null>(null);

// Componente interno que carrega o script quando a API key está disponível
const GoogleMapsLoader = ({ apiKey, children }: { apiKey: string; children: ReactNode }) => {
  const { isLoaded, loadError } = useLoadScript({
    id: 'google-maps-script',
    googleMapsApiKey: apiKey,
    libraries,
    preventGoogleFontsLoading: true,
  });

  return (
    <GoogleMapsContext.Provider value={{ 
      isLoaded, 
      loadError, 
      apiKey, 
      isConfigLoading: false,
      configError: null 
    }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

// Provider que NÃO bloqueia a UI - sempre renderiza children
export const GoogleMapsProvider = ({ children }: { children: ReactNode }) => {
  const { data: config, isLoading: configLoading, error: configError } = usePublicConfig();
  const apiKey = config?.googleMapsApiKey || '';

  // Se está carregando config ou não tem API key, renderiza children com contexto indicando loading
  if (configLoading || !apiKey) {
    return (
      <GoogleMapsContext.Provider value={{ 
        isLoaded: false, 
        loadError: undefined, 
        apiKey: '',
        isConfigLoading: configLoading,
        configError: configError || null
      }}>
        {children}
      </GoogleMapsContext.Provider>
    );
  }

  // API key disponível - carrega o script do Google Maps
  return <GoogleMapsLoader apiKey={apiKey}>{children}</GoogleMapsLoader>;
};

export const useGoogleMapsContext = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMapsContext must be used within GoogleMapsProvider');
  }
  return context;
};
